import { navigate } from '@reach/router';
import { graphql, Link } from 'gatsby';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import BodyClassName from 'react-body-classname';

import renderHtml from 'htmr';
import { decode } from 'html-entities';
import { useFetch } from '../../hooks';
import SocialShare from '../../components/Shop/SocialShare';
import DownChain from '../../images/svg-gcbooks/down-chain';
import WatchIcon from '../../images/svg-gcbooks/watch-icon';
import { AppContext } from '../../apollo/wrap-root-element';
import Backdrop from '../../components/accessories/backdrop';
import BookIconWhite from '../../images/svg-gcbooks/book-icon';
import NextIconWhite from '../../images/svg-gcbooks/next-icon';
import PrevIconWhite from '../../images/svg-gcbooks/prev-icon';
import ShareMenu from '../../components/accessories/shareMenu';
import CloseIcongc from '../../images/svg-gcbooks/close-icongc';
import ShareIconWhite from '../../images/svg-gcbooks/share-icon';
import ReadPageContent from '../../components/Read/ReadPageContent';
import { AuthorSection } from '../../components/BookChapters/Author-section';
import ChainDisabled from '../../images/svg-gcbooks/chain-disabled';
import CollapseButton from '../../images/svg-gcbooks/collapse-button';
import DefaultLayoutComponent from '../layouts/DefaultLayoutComponent';
import NextArrow from '../../images/svg-gcbooks/next-arrow';
import PlayIcongc from '../../images/svg-gcbooks/playIcongc';
import { getGcRelatedContentHref, getSliceNumber } from '../../utils/watch';
import GCBook from '../../images/feature_books/gc-ad-thumbnail.png';
import GCBook2 from '../../images/svg-gcbooks/GCBook.png';
import UpLeftArrow from '../../images/svg-gcbooks/arrow-up-right.svg';

import { COLOR_GRAY, FEATURED_SCROLLID, FOOTER_VIDEO_SCROLLID, VIDEO_SCROLLID } from '../../utils/constants';
import ReadIcongc from '../../images/svg-gcbooks/read-icongc';
import ListengcIcon from '../../images/svg-gcbooks/listengcIcon';
import SkeletonText from '../../components/Skelton/SkeletonText';
import { getSlugFromUid } from '../../utils/title-case';
import { getFullChapterPublicPath, getGcUi, sortChaptersByNumber, findChapterIndex, getChapterDisplayNumber, filterChapterNodesForGcLocale, isSpanishGcPage, getGcBackPath, getStudyChapterNavigatePath, getLoginPath, buildFullReadUidMap, getDedicatedFullReadUid, shouldUseFullReadPage, shouldShowReadAction } from '../../constants/spanish-gc';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import ReadListenButton from './readListenButton';
import VoiceSelector from './voiceSelector';
import AudioToggleButton from './audioToggleButton';
import { useBookAudioPlayer } from '../../context/bookAudioPlayerContext';

const isListEmpty = (arr) => {
  if (Array.isArray(arr) && !!arr?.length) {
    return false;
  }

  return true;
};

export default function BookChapterPageTemplate({ data, pageContext }) {
  const chapterData = data?.allPrismicBookChapter?.nodes?.[0]?.data || {};

  const {
    relatedReads,
    relatedListens,
    featuredVideoThumbnailImage,
    footerVideoThumbnailImage,
    chapterTitle,
    furtherReadings,
    footnotes,
    footerVideo,
    discussionQuestions,
    featuredVideo,
    displayPriority,
    timelineLink,
    bookText,
    relatedEpisodes,
    featuredVideoDuration,
    shortDescription,
    additionalVideos,
    audioByCharlene,
    audioByCallie,
    audioByNancy,
  } = chapterData;

  const sliceData = data?.allPrismicBookChapter?.nodes[0]?.data?.body;

  const title = data?.allPrismicBookChapter?.nodes[0]?.data?.chapterTitle?.text;
  const uid = data?.allPrismicBookChapter?.nodes[0]?.uid;
  const isSpanish = isSpanishGcPage(pageContext, uid);
  const ui = getGcUi(isSpanish ? 'es' : pageContext?.bookUid);
  const bookHomePath = getGcBackPath(pageContext, uid);
  const furtherReadingList = furtherReadings?.map((fReading) => fReading?.furtherReading?.text).filter((e) => e);
  const footNotesList = footnotes?.map((elem) => elem?.footnote?.text).filter((e) => e);
  const allChapters = useMemo(
    () => sortChaptersByNumber(filterChapterNodesForGcLocale(data?.chapters?.nodes, isSpanish)),
    [data?.chapters?.nodes, isSpanish]
  );
  const resolvedChapterIndex = Math.max(0, findChapterIndex(allChapters, uid));
  const nextChapter =
    resolvedChapterIndex >= 0 && resolvedChapterIndex < allChapters.length - 1
      ? allChapters[resolvedChapterIndex + 1]
      : null;
  const bookTitle = allChapters[0]?.data?.chapterOfBook?.document?.data?.title?.text;
  const featuredVideoThumbnailImageUrl = featuredVideoThumbnailImage?.fluid?.src;
  const footerVideoThumbnailImageUrl = footerVideoThumbnailImage?.fluid?.src;
  const chapterText = bookText?.document?.data?.chapterText?.text;
  const fullReadUidByNumber = useMemo(
    () => buildFullReadUidMap(data?.fullReadChapters?.nodes ?? [], isSpanish),
    [data?.fullReadChapters?.nodes, isSpanish]
  );
  const dedicatedFullReadUid = useMemo(
    () => getDedicatedFullReadUid(uid, fullReadUidByNumber, isSpanish),
    [uid, fullReadUidByNumber, isSpanish]
  );
  const useFullReadPage = shouldUseFullReadPage({
    chapterIndex: resolvedChapterIndex,
    dedicatedFullReadUid,
  });
  const showReadAction = shouldShowReadAction({
    chapterIndex: resolvedChapterIndex,
    chapterText,
    dedicatedFullReadUid,
  });
  const fullReadPath = getFullChapterPublicPath(dedicatedFullReadUid || uid);

  const [contractContent, setContractContent] = useState(true);
  const [showReadModal, setShowReadModal] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const { currentChapterIndex, setCurrentChapterIndex, currentUserDetails } = React.useContext(AppContext);
  const videScrollID = [FEATURED_SCROLLID, VIDEO_SCROLLID, FOOTER_VIDEO_SCROLLID];
  const [currentVideoID, setCurrentVideoID] = useState(1);
  const [showFooterNav, setShowFooterNav] = useState(false);
  const [completeBookForUser, completeBookResponse, error, loading] = useFetch('POST');
  const { setChaptersQueue } = useBookAudioPlayer();

  const { isLoggedIn } = useCurrentUser();

  const audioDetails = {
    audioByCharleneId: audioByCharlene,
    audioByCallieId: audioByCallie,
    audioByNancyId: audioByNancy,
    audioTitle: chapterTitle?.text,
    index: currentChapterIndex,
  };

  // const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  // const [hasAccess, setHasAccess] = useState(false);
  // const hasRedirected = useRef(false);

  const relatedEpisodesWithDetails = relatedEpisodes
    ?.filter((content) => content?.relatedEpisode?.uid)
    .map((item) => {
      const watch = data.totalWatchData.nodes.find((watch) => watch.uid === item.relatedEpisode.uid);
      return {
        ...watch.data,
        category: 'WATCH',
        uid: item.relatedEpisode.uid,
        title: watch.data.title[0].text,
        duration: watch.data.videoDuration[0].text,
        imageUrl: watch.data.thumbnailImage.url,
      };
    });

  const relatedReadsWithDetails = relatedReads
    ?.filter((content) => content?.relatedRead?.uid)
    .map((item) => {
      const read = data.readData.nodes.find((read) => read.uid === item?.relatedRead?.uid);
      return {
        ...read.data,
        category: 'ARTICLE',
        uid: read.uid,
        url: read.url,
        title: read.data.title.text,
        duration: read.data.readDuration.text,
        imageUrl: read.data.featuredImage.url,
      };
    });

  const relatedListensWithDetails = relatedListens
    ?.filter((content) => content?.relatedListen?.uid)
    .map((item) => {
      const podcast = data.podCastData.nodes.find((podcast) => podcast.uid === item.relatedListen.uid);
      return {
        ...podcast.data,
        category: 'LISTEN',
        uid: item.relatedListen.uid,
        title: podcast.data.podCastTitle[0].text,
        imageUrl: podcast.data.thumnailImage.url,
        duration: podcast.podcastDuration,
      };
    });

  const relatedReadCount = relatedReadsWithDetails?.length;
  const relatedEpisodesCount = relatedEpisodesWithDetails?.length;
  const relatedListenCount = relatedListensWithDetails?.length;

  let relatedData = [];

  if (relatedEpisodes?.length) {
    const sliceNumber = getSliceNumber(relatedEpisodesCount, relatedListenCount, relatedData, 3);
    relatedData = relatedData.concat(relatedEpisodesWithDetails.slice(0, sliceNumber));
  }
  if (relatedReadCount) {
    const sliceNumber = getSliceNumber(relatedEpisodesCount, relatedListenCount, relatedData, 3);
    relatedData = relatedData.concat(relatedReadsWithDetails.slice(0, sliceNumber));
  }

  if (relatedListenCount) {
    const sliceNumber = getSliceNumber(relatedReadCount, relatedEpisodesCount, relatedData, 3);
    relatedData = relatedData.concat(relatedListensWithDetails.slice(0, sliceNumber));
  }

  const handleNextPrev = (type) => {
    const chapterPath = (chapterUid) => getStudyChapterNavigatePath(chapterUid, isSpanish);

    if (currentChapterIndex === 0 && type === 'prev') return;
    if (currentChapterIndex === allChapters?.length - 1 && type === 'next') return;

    if (type === 'next') {
      if (currentChapterIndex === allChapters?.length - 1) {
        setCurrentChapterIndex(0);
        navigate(chapterPath(allChapters[0]?.uid));
      } else {
        setCurrentChapterIndex(currentChapterIndex + 1);
        navigate(chapterPath(allChapters[currentChapterIndex + 1]?.uid));
      }

      return;
    }
    if (currentChapterIndex === 0) {
      setCurrentChapterIndex(allChapters?.length - 1);
      navigate(chapterPath(allChapters[allChapters?.length - 1]?.uid));
    } else {
      setCurrentChapterIndex(currentChapterIndex - 1);
      navigate(chapterPath(allChapters[currentChapterIndex - 1]?.uid));
    }
  };

  // useEffect(() => {
  //   // Capture signals immediately when page loads
  //   const referrer = document.referrer.toLowerCase();
  //   const userAgent = navigator.userAgent.toLowerCase();
  //   const currentDomain = window.location.hostname;

  //   const isFromQRService = referrer.includes('qrco.de');
  //   const isInAppBrowser = userAgent.includes('wv') ||
  //     userAgent.includes('line') ||
  //     userAgent.includes('fbav') ||
  //     userAgent.includes('instagram') ||
  //     userAgent.includes('fban');
  //   const hasExternalReferrer = referrer && !referrer.includes(currentDomain);
  //   const isFirstVisit = !localStorage.getItem('has_visited_site');

  //   // Score-based approach
  //   let qrScore = 0;
  //   if (isFromQRService) qrScore += 3;
  //   if (isInAppBrowser) qrScore += 2;
  //   if (hasExternalReferrer && !referrer.includes('google') && !referrer.includes('facebook')) qrScore += 1;
  //   if (isFirstVisit) qrScore += 1;

  //   // Grant QR access if score is high enough
  //   if (qrScore >= 2) {
  //     sessionStorage.setItem('qr_access', 'true');
  //     sessionStorage.setItem('qr_access_time', Date.now().toString());
  //   }

  //   // Mark as visited
  //   if (isFirstVisit) {
  //     localStorage.setItem('has_visited_site', 'true');
  //   }
  // }, []);

  useEffect(() => {
    const chapterIndex = findChapterIndex(allChapters, uid);

    if (chapterIndex === -1) {
      return;
    }

    setCurrentChapterIndex((prev) => (prev === chapterIndex ? prev : chapterIndex));
  }, [allChapters, uid, setCurrentChapterIndex]);

  useEffect(() => {
    if (currentUserDetails?.customer?.id && data?.allPrismicBookChapter?.nodes?.length) {
      completeBookForUser('api/postCompletedBook', {
        body: {
          bookId: data?.allPrismicBookChapter?.nodes[0].uid,
          userId: currentUserDetails?.customer?.id,
        },
      });
    }
  }, []);

  const [contentMenuRightPosition, setContentMenuRightPosition] = useState(0);
  let divElement, windowWidth, gcContainerWidth;
  useEffect(() => {
    if (typeof window != 'undefined') {
      divElement = document.getElementById(videScrollID[currentVideoID]);
      windowWidth = window.innerWidth;
      gcContainerWidth = document.getElementById('gcContainerID')?.offsetWidth - 40;
      setContentMenuRightPosition(windowWidth - gcContainerWidth);
    }
  }, []);
  if (typeof window != 'undefined') {
    divElement = document.getElementById(videScrollID[currentVideoID]);
  }
  const scroll = () => {
    divElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  };

  //share via native share

  let resultPara, shareUrl;
  if (typeof window !== 'undefined') {
    resultPara = document.querySelector('.result');
    shareUrl = window.location && window?.location?.href;
  }
  const shareData = {
    title: ui.share,
    text: '',
    url: '',
  };

  const handleShareNative = async () => {
    shareData.url = shareUrl;
    try {
      await navigator.share(shareData);
    } catch (err) {
      console.error(err);
    }
  };

  const relatedContentIcons = useMemo(
    () => ({
      WATCH: {
        icon: <WatchIcon />,
        todo: ui.categoryWatch,
        url: '/watch',
      },
      ARTICLE: {
        icon: <ReadIcongc />,
        todo: ui.categoryRead,
        url: '/read',
      },
      LISTEN: {
        icon: <ListengcIcon />,
        todo: ui.categoryListen,
        url: '/podcast',
      },
    }),
    [ui]
  );

  var seoData = {
    canonical: sliceData?.uri,
    title: (chapterTitle?.text || '') + ` - ${ui.seoTitleSuffix}`,
    metaDesc: shortDescription?.text || `Read more about ${ui.seoTitleSuffix} - ${chapterTitle?.text || ''}`,
    opengraphDescription:
      shortDescription?.text || `Read more about ${ui.seoTitleSuffix} - ${chapterTitle?.text || ''}`,
    twitterDescription: shortDescription?.text || `Read more about ${ui.seoTitleSuffix} - ${chapterTitle?.text || ''}`,
    opengraphTitle: chapterTitle?.text || '',
    twitterTitle: chapterTitle?.text || '',
    opengraphImage: {
      sourceUrl: featuredVideoThumbnailImageUrl?.fluid?.src,
    },
    twitterImage: {
      sourceUrl: featuredVideoThumbnailImageUrl?.fluid?.src,
    },
    opengraphUrl: sliceData?.uri || '',
  };

  // Show loading state while checking access
  // if (isCheckingAccess) {
  //   return (
  //     <BodyClassName className="page-read page-read--single body-dark page-watch">
  //       <DefaultLayoutComponent backUrl="/books/the-great-controversy" timeline={true} gcbooks={true} seo={seoData}>
  //         <div className="wrapper wrapper-xl pd-x-md" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
  //           <div className="modern-loader-wrapper">
  //             <span class="model-spinner-loader"></span>

  //             <p className="loader-text">Verifying access...</p>
  //           </div>
  //         </div>
  //       </DefaultLayoutComponent>
  //     </BodyClassName>
  //   );
  // }

  // if (!hasAccess) {
  //   return null;
  // }
  const chaptersQueueKey = useMemo(() => allChapters.map((chapter) => chapter.uid).join('|'), [allChapters]);
  const lastChaptersQueueKeyRef = useRef('');

  useEffect(() => {
    if (!allChapters.length) return;
    if (lastChaptersQueueKeyRef.current === chaptersQueueKey) return;

    lastChaptersQueueKeyRef.current = chaptersQueueKey;
    setChaptersQueue(
      allChapters.map((chapter, index) => ({
        uid: chapter.uid,
        audioByCharleneId: chapter.data?.audioByCharlene,
        audioByCallieId: chapter.data?.audioByCallie,
        audioByNancyId: chapter.data?.audioByNancy,
        audioTitle: chapter.data?.chapterTitle?.text,
        index,
      }))
    );
  }, [allChapters, chaptersQueueKey, setChaptersQueue]);

  return (
    <BodyClassName className="  page-read page-read--single body-dark page-watch">
      <DefaultLayoutComponent backUrl={bookHomePath} timeline={true} gcbooks={true} seo={seoData}>
        <div className="bookChapterPageContainer">
          <div className={showReadModal ? 'readChapterModal' : 'hide'}>
            <div className="gcmodalCloseButton" onClick={() => setShowReadModal(false)}>
              <CloseIcongc />
            </div>
            <h3
              style={{
                marginBottom: '10px',
              }}
            >
              {ui.readModalChapter(currentChapterIndex + 1, chapterTitle?.text)}
            </h3>
            <div className="textContentGC"> {renderHtml(decode(chapterText))}</div>
          </div>
          <Backdrop
            BackdropStatus={showReadModal ? true : false}
            onBackDropClick={setShowReadModal}
            backdropPayload={false}
          />
          <div
            className="wrapper wrapper-xl pd-x-md displayflexContentMenu"
            id="gcContainerID"
            style={{
              display: 'flex',
              gap: '20px',
            }}
          >
            <div className="gcBooksChapterChain" id={`${contractContent ? '' : 'gcBooksChapterChainContract'}`}>
              <div
                className={`${contractContent ? 'gcBooksCollapseButton' : 'gcBooksExpandButton'}`}
                onClick={() => setContractContent(!contractContent)}
              >
                <CollapseButton />
              </div>

              <div className={`${contractContent ? 'chapterChainHeader' : 'chapterChainHeader flex-c'}`}>
                <h3>{contractContent ? ui.chaptersNavLabel : ui.chaptersNavShort}</h3>
                {contractContent ? (
                  <h5>
                    {ui.readProgressLabel}{' '}
                    <span className="textYellow">{currentChapterIndex + 1}</span> {ui.readProgressOf}{' '}
                    {allChapters?.length}
                  </h5>
                ) : (
                  <h5>
                    <span className="textYellow">{currentChapterIndex + 1}</span> /{allChapters?.length}
                  </h5>
                )}
              </div>
              <div className="gcBooksChaptersContainer">
                {allChapters?.map((chapter, idx) => {
                  const title = chapter?.data?.chapterTitle?.text;
                  const isSigninRequired = !isLoggedIn;
                  const chapterPath = getStudyChapterNavigatePath(chapter?.uid, isSpanish);
                  const lockedStateLoginUrl =
                    isSigninRequired && getLoginPath(chapterPath);
                  const destination =
                    isSigninRequired && idx > 3 ? lockedStateLoginUrl : chapterPath;

                  return (
                    <div key={chapter?.uid || idx} className="chapterAndChain">
                      {isSigninRequired && idx === 4 && (
                        <Link to={lockedStateLoginUrl} className="signintext">
                          {ui.signInToContinue}
                        </Link>
                      )}
                      <i className="chapterChainIcon">
                        <ChainDisabled
                          color={idx <= currentChapterIndex ? 'selected' : 'disabled'}
                          fill={idx < currentChapterIndex ? 'selected' : 'disabled'}
                        />
                      </i>
                      <Link
                        to={destination}
                        className={`${contractContent ? '' : 'slide'}`}
                        onClick={() => setCurrentChapterIndex(idx)}
                      >
                        <h5
                          className={
                            idx < currentChapterIndex
                              ? 'disabledText'
                              : isSigninRequired && idx > 3 && idx !== currentChapterIndex
                              ? 'disabledText--light'
                              : idx === currentChapterIndex
                              ? 'textYellow'
                              : ''
                          }
                        >
                          {ui.chAbbrev(idx + 1)} - {title}
                        </h5>
                      </Link>
                      {idx === allChapters?.length - 1 ? (
                        ''
                      ) : (
                        <div className={`${idx < currentChapterIndex ? 'gcChainLineYellow' : 'gcChainLine'}`}></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div
              className={`${
                contractContent
                  ? 'bookChapterContentWrapper  contractContent '
                  : 'bookChapterContentWrapper expandContent'
              }`}
            >
              <div className="chapterPageHeader ">
                {currentChapterIndex !== null && chapterTitle?.text ? (
                  <div className="chapterPageHeaderContent">
                    <div className="chapterPageHeaderContentResp">
                      <div className="chapterPageHeaderTitles">
                        <p>{ui.chapterPageHeader(getChapterDisplayNumber(uid, resolvedChapterIndex))}</p>
                        <h3>{chapterTitle.text}</h3>
                      </div>
                      <div className=" chapterPageMenuResp menuItemsChapterPage">
                        {/* <Link to={`/timeline-details/event/${timelineLink?.uid}`}>
                    <div className="menuItemChapterPage">
                      <WatchIcon color={COLOR_GRAY} /> <h5>View timeline</h5>
                    </div>
                  </Link> */}
                        {/* {chapterText ? (
                    <div className="menuItemChapterPage cursor-pointer" onClick={() => setShowReadModal(true)}>
                      <BookIconWhite color={COLOR_GRAY} />
                      <h5>Read Chapter</h5>
                    </div>
                  ) : (
                    ''
                  )} */}
                        <div
                          className="cursor-pointer"
                          style={{ marginBottom: '50px' }}
                          onClick={() => handleShareNative()}
                        >
                          <ShareIconWhite color={COLOR_GRAY} />
                        </div>
                      </div>
                    </div>
                    <div className="tablet-responsive">
                      <ReadListenButton
                        audioDetails={audioDetails}
                        setShowReadModal={setShowReadModal}
                        chapterUid={uid}
                        useFullReadPage={useFullReadPage}
                        fullReadPath={fullReadPath}
                      />
                      <div className="chapterPageMenuResp">
                        <div className="voice-selection-dropdown">
                          <p>{ui.voicePrompt}</p>
                          <VoiceSelector />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <SkeletonText />
                )}
              </div>
              <ReadPageContent
                body={sliceData}
                showReadModal={showReadModal}
                showShareMenu={showShareMenu}
                setShowReadModal={setShowReadModal}
                setShowShareMenu={setShowShareMenu}
                audioDetails={audioDetails}
                from="gc"
                locale={isSpanish ? 'es' : 'en'}
                chapterUid={uid}
                useFullReadPage={useFullReadPage}
                fullReadPath={fullReadPath}
                showReadAction={showReadAction}
                discussionQuestions={discussionQuestions}
                featuredVideo={featuredVideo}
                footerVideo={footerVideo}
                featuredVideoDuration={featuredVideoDuration}
                footerVideoThumbnailImage={footerVideoThumbnailImageUrl}
                featuredVideoThumbnailImage={featuredVideoThumbnailImageUrl}
                chapterTitle={chapterTitle}
                additionalVideos={additionalVideos}
              />
              <div className="read-footer bg-transparent">
                <div className="wrapper wrapper-xl bg-white" id="pd-x-mdgc">
                  <div
                    style={{
                      paddingBottom: '30px',
                    }}
                    className="r_s_footer-info pd-t-0 pd-x-sm"
                  >
                    <div className="wrapper wrapper-md pd-x-sm">
                      <div className="wrapper-sm">
                        <AuthorSection locale={isSpanish ? 'es' : 'en'} />
                        <div className="gc-chapter-footer-actions">
                          <SocialShare title={title} className="gc-chapter-footer-actions__share" />
                          {showReadAction &&
                            (useFullReadPage ? (
                              <Link to={fullReadPath} className="gcslogan gc-chapter-footer-actions__read">
                                {ui.readChapterIn(bookTitle)}
                              </Link>
                            ) : (
                              <button
                                type="button"
                                className="gcslogan gc-chapter-footer-actions__read"
                                onClick={() => setShowReadModal(true)}
                              >
                                {ui.readChapterIn(bookTitle)}
                              </button>
                            ))}
                        </div>
                        {!isListEmpty(furtherReadingList) && (
                          <div className="r_s_author">
                            <h6 className="r_s_author-name">{ui.furtherReading}</h6>
                            {furtherReadings.map((fReading) => (
                              <p key={fReading?.furtherReading?.text} className="r_s_author-desc">
                                {fReading?.furtherReading?.text}
                              </p>
                            ))}
                          </div>
                        )}
                        {!isListEmpty(footNotesList) && (
                          <div className="r_s_author">
                            <h6 className="r_s_author-name">{ui.footnotes}</h6>
                            {footnotes.map((fnote) => (
                              <p key={fnote?.footnote?.text} className="r_s_author-desc">
                                {fnote?.footnote?.text}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {nextChapter && (
              <div className="wrapper wrapper-xl pd-x-md gcNextChapter">
                <div
                  className="nextChapterContainer"
                  style={{
                    backgroundImage: `url(${nextChapter?.data?.thumbnailImage?.url || ''})`,
                  }}
                >
                  <div className="nextChapterOverlay">
                    <div className="nextChapterContent">
                      <div className="nextChapterTextWrapper">
                        <div className="nextChapterLabel">{ui.nextChapter}</div>
                        <h3 className="nextChapterTitle">{nextChapter?.data?.chapterTitle?.text}</h3>
                        <p className="nextChapterDescription">{nextChapter?.data?.shortDescription?.text}</p>
                      </div>

                      <Link
                        to={getStudyChapterNavigatePath(nextChapter?.uid, isSpanish)}
                        className="nextChapterButton"
                      >
                        {ui.next} <NextArrow />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              )}
              <div className="gcBooksRelatedContents">
                <h4>{ui.relatedContents}</h4>
                <div className="gcBooksRelatedContentItems">
                  {/* <RelatedContent relatedContent={relatedData} /> */}
                  {relatedData?.map((item) => {
                    const title = item?.title;
                    const imageUrl = item?.imageUrl;
                    const uid = item?.uid;
                    const durations = item?.duration;
                    const itemHref = getGcRelatedContentHref({
                      category: item?.category,
                      uid,
                      url: item?.url,
                    });
                    const itemKey = uid || item?.url || `${item?.category}-${title}`;
                    const photo = (
                      <div className="gcBooksRelatedContentphoto">
                        <img src={imageUrl} alt={title} />
                        {durations && (
                          <div className="contentTime">
                            {durations} {item?.category === 'ARTICLE' ? ui.minRead : ''}
                          </div>
                        )}
                      </div>
                    );
                    return (
                      <div key={itemKey} className="gcBooksRelatedContentItem">
                        {item?.category && (
                          <Link to={relatedContentIcons[item?.category]?.url}>
                            <div className="category-tag-gc">
                              {relatedContentIcons[item?.category]?.icon}
                              <h6>{relatedContentIcons[item?.category]?.todo}</h6>
                            </div>
                          </Link>
                        )}
                        {itemHref ? <Link to={itemHref}>{photo}</Link> : photo}
                        <div className="gcBooksRelatedContentTitle">
                          <h5>{title}</h5>
                          <p>{item.category}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="chapterContentMenu" style={{ right: `${contentMenuRightPosition / 2}px` }}>
              <div className="chapterPageSharemenu">
                <div className="chapterPagePrevNext">
                  <div
                    className="prevNextButton "
                    onClick={() => {
                      handleNextPrev('prev');
                    }}
                  >
                    <PrevIconWhite active={currentChapterIndex === 0 ? false : true} /> <h6>{ui.prevShort}</h6>
                  </div>
                  {/* <Link to={`/books/${allChapters[currentChapterIndex + 1]?.uid}`}> */}
                  <div
                    className="prevNextButton "
                    onClick={() => {
                      handleNextPrev('next');
                    }}
                  >
                    <h6>{ui.nextShort}</h6>
                    <NextIconWhite active={currentChapterIndex === allChapters?.length - 1 ? false : true} />
                  </div>
                  {/* </Link> */}
                </div>

                <div className="menuItemsChapterPage">
                  {timelineLink?.uid && (
                    <Link to={`/timeline-details/event/${timelineLink?.uid}`} className="margin-b-32">
                      <div className="menuItemChapterPage">
                        <WatchIcon /> <h6>{ui.viewTimeline}</h6>
                      </div>
                    </Link>
                  )}
                  <div className="menuItemChapterPage margin-b-32" onClick={() => setShowShareMenu(true)}>
                    <ShareIconWhite /> <h6>{ui.shareChapter}</h6>
                    {showShareMenu ? <ShareMenu /> : ''}
                  </div>
                  <Backdrop
                    BackdropStatus={showShareMenu ? true : false}
                    onBackDropClick={setShowShareMenu}
                    backdropPayload={false}
                  />
                  {showReadAction ? (
                    useFullReadPage ? (
                      <Link to={fullReadPath} className="menuItemChapterPage margin-b-32">
                        <BookIconWhite color="#fecb04" />
                        <h6>{ui.readChapter}</h6>
                      </Link>
                    ) : (
                      <div
                        onClick={() => setShowReadModal(true)}
                        role="button"
                        className="menuItemChapterPage margin-b-32"
                      >
                        <BookIconWhite color="#fecb04" />
                        <h6>{ui.readChapter}</h6>
                      </div>
                    )
                  ) : (
                    ''
                  )}
                  <AudioToggleButton text={ui.listenToChapter} audioDetails={audioDetails} />
                </div>
              </div>

              <div className="voice-selection-dropdown">
                {ui.voicePrompt}
                <VoiceSelector />
              </div>
              <div
                className="next-video-btn"
                onClick={() => {
                  scroll();
                  setCurrentVideoID(currentVideoID === 2 ? 0 : currentVideoID + 1);
                }}
              >
                <PlayIcongc />
                <h6>{ui.nextVideo}</h6>
              </div>

              <div className="gc-link-container">
                <a
                  href="https://lineagejourney.com/product/the-illustrated-great-controversy"
                  target="_blank"
                  className="gc-link"
                >
                  <div className="arrow-image-container">
                    <img className="cover-img" src={GCBook} />
                    <div>
                      <img src={UpLeftArrow} />
                    </div>
                  </div>

                  <p className="gc-link-text link-header">{ui.illustratedGcTitle}</p>

                  <p className="gc-link-text link-sub">{ui.illustratedGcLinkSub}</p>
                </a>
              </div>

              <div className="gc-link-container">
                <a href="https://www.freegc.org/" target="_blank" className="gc-link">
                  <div className="arrow-image-container">
                    <img className="cover-img" src={GCBook2} />
                    <div>
                      <img src={UpLeftArrow} />
                    </div>
                  </div>

                  <p className="gc-link-text link-header">{ui.getFreeCopy}</p>

                  <p className="gc-link-text link-sub">{ui.freeGcLinkSub}</p>
                </a>
              </div>
            </div>
          </div>
          <div className=" gcFooterMenu">
            {/* <div className="gcBooksChapterChainFooter " id="gcBooksChapterChainContract"> */}
            <div className="icon-footer-watch">
              <i className="res-watch-icon">
                <WatchIcon />
              </i>

              <div
                className="chapterChainHeader flex-c cursor-pointer marginl-10 res-m0"
                onClick={() => setShowFooterNav(!showFooterNav)}
              >
                <h3>{ui.chaptersLower}</h3>

                {contractContent ? (
                  <h5>
                    {ui.readProgressLabel} <span className="textYellow">{currentChapterIndex + 1}</span> {ui.readProgressOf}{' '}
                    {allChapters?.length}
                  </h5>
                ) : (
                  <h5>
                    <span className="textYellow">{currentChapterIndex + 1}</span> /{allChapters?.length}
                  </h5>
                )}
              </div>
            </div>
            {/* </div> */}
            <div className="chapterPagePrevNext">
              <div
                className="prevNextButton "
                onClick={() => {
                  handleNextPrev('prev');
                }}
              >
                <PrevIconWhite active={currentChapterIndex === 0 ? false : true} /> <h6>{ui.prevShort}</h6>
              </div>
              {/* <Link to={`/books/${allChapters[currentChapterIndex + 1]?.uid}`}> */}
              <div
                className="prevNextButton "
                onClick={() => {
                  handleNextPrev('next');
                }}
              >
                <h6>{ui.nextShort}</h6>
                <NextIconWhite active={currentChapterIndex === allChapters?.length - 1 ? false : true} />
              </div>
              {/* </Link> */}
            </div>
            {/* menu */}
            <div className={showFooterNav ? 'footerMenuOuter' : 'hide'}>
              <div className="minimize-menu" onClick={() => setShowFooterNav(false)}>
                <CloseIcongc color={COLOR_GRAY} />
              </div>
              <div
                className="gcBooksChapterChainMenuFooter"
                id={`${contractContent ? '' : 'gcBooksChapterChainContract'}`}
              >
                {/* <div
                className={`${contractContent ? 'gcBooksCollapseButton' : 'gcBooksExpandButton'}`}
                onClick={() => setContractContent(!contractContent)}
              >
                <CollapseButton />
              </div> */}
                <div className="chapterChainHeader ">
                  <h3>{ui.chaptersLower}</h3>
                  {contractContent ? (
                    <h5>
                      {ui.readProgressLabel} <span className="textYellow">{currentChapterIndex + 1}</span> {ui.readProgressOf}{' '}
                      {allChapters?.length}
                    </h5>
                  ) : (
                    <h5>
                      <span className="textYellow">{currentChapterIndex + 1}</span> /{allChapters?.length}
                    </h5>
                  )}
                </div>
                <div className="gcBooksChaptersContainer  height-menu">
                  {allChapters?.map((chapter, idx) => {
                    const title = chapter?.data?.chapterTitle?.text;
                    const chapterPath = getStudyChapterNavigatePath(chapter?.uid, isSpanish);
                    return (
                      <div key={chapter?.uid || idx} className="chapterAndChain">
                        <i className="chapterChainIcon">
                          <ChainDisabled
                            color={idx <= currentChapterIndex ? 'selected' : 'disabled'}
                            fill={idx < currentChapterIndex ? 'selected' : 'disabled'}
                          />
                        </i>
                        <Link
                          to={chapterPath}
                          className={`${contractContent ? '' : 'slide'}`}
                          onClick={() => setCurrentChapterIndex(idx)}
                        >
                          <h5
                            className={
                              idx < currentChapterIndex
                                ? 'disabledText'
                                : idx === currentChapterIndex
                                ? 'textYellow'
                                : ''
                            }
                          >
                            {ui.chAbbrev(idx + 1)} - {title}
                          </h5>
                        </Link>
                        {idx === allChapters?.length - 1 ? (
                          ''
                        ) : (
                          <div className={`${idx < currentChapterIndex ? 'gcChainLineYellow' : 'gcChainLine'}`}></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DefaultLayoutComponent>
    </BodyClassName>
  );
}

export const query = graphql`
  query SingleChapterPageQuery($uid: String!, $bookUid: String!) {
    fullReadChapters: allPrismicBookChapter(
      filter: { data: { is_full_chapter: { eq: true } } }
    ) {
      nodes {
        uid
      }
    }
    chapters: allPrismicBookChapter(
      sort: { fields: data___display_priority, order: ASC }
      filter: {
        data: { is_full_chapter: { ne: true }, chapter_of_book: { uid: { eq: $bookUid } } }
      }
    ) {
      nodes {
        data {
          thumbnailImage: thumbnail_image {
            fluid {
              src
            }
            fixed {
              src
            }
            url
          }
          chapterOfBook: chapter_of_book {
            document {
              ... on PrismicBooks {
                id
                data {
                  title {
                    text
                  }
                }
              }
            }
          }
          audioByCharlene: audio_id_charlene
          audioByCallie: audio_id_callie
          audioByNancy: audio_id_nancy
          chapterTitle: chapter_title {
            text
          }
          shortDescription: short_description {
            text
          }
        }
        uid
      }
    }
    allPrismicBookChapter(filter: { uid: { eq: $uid }, data: { is_full_chapter: { ne: true } } }) {
      nodes {
        uid
        data {
          relatedReads: related_reads {
            relatedRead: related_read {
              id
              uid
            }
          }
          additionalVideos: additional_video {
            thumbnail {
              alt
              fluid(maxWidth: 1200) {
                ...GatsbyPrismicImageFluid
              }
            }
            title {
              text
            }
            duration {
              text
            }
            youtubeId: youtube_id {
              text
            }
          }

          relatedEpisodes: related_episodes {
            relatedEpisode: related_episode {
              id
              uid
            }
          }
          relatedListens: related_listens {
            relatedListen: related_listen {
              id
              uid
            }
          }
          featuredVideoThumbnailImage: featured_video_thumbnail_image {
            fluid {
              src
            }
          }
          footerVideoThumbnailImage: footer_video_thumbnail_image {
            fluid {
              src
            }
          }
          featuredVideoDuration: featured_video_duration {
            text
          }
          bookText: book_text {
            document {
              ... on PrismicChapterText {
                id
                data {
                  chapterText: chapter_text {
                    text
                  }
                }
              }
            }
          }
          displayPriority: display_priority
          timelineLink: timeline_link {
            uid
          }
          audioByCharlene: audio_id_charlene
          audioByCallie: audio_id_callie
          audioByNancy: audio_id_nancy
          body {
            ... on PrismicBookChapterBodyText {
              id
              sliceType: slice_type
              primary {
                backgroundColor: background_color
                sectionContents: section_contents {
                  html
                }
              }
            }
            ... on PrismicBookChapterBodyPhoto {
              id
              items {
                image {
                  alt
                  fluid(maxWidth: 1200) {
                    ...GatsbyPrismicImageFluid
                  }
                }
                caption {
                  text
                }
              }
              sliceType: slice_type
              primary {
                backgroundColor: background_color
              }
            }
            ... on PrismicBookChapterBodyQuote {
              id
              primary {
                backgroundColor: background_color
                sectionContents: section_contents {
                  text
                }
              }
              sliceType: slice_type
            }
            ... on PrismicBookChapterBodyVideo {
              id
              primary {
                sliceVideoThumbnail: slice_video_thumbnail {
                  fluid {
                    src
                  }
                }
                backgroundColor: background_color
                videoId: video_id {
                  text
                }
              }
              sliceType: slice_type
            }
            ... on PrismicBookChapterBodyMapEmbedUrl {
              id
              sliceType: slice_type
            }
          }
          chapterTitle: chapter_title {
            text
          }
          shortDescription: short_description {
            text
          }
          featuredVideo: feature_video_id {
            text
          }

          furtherReadings: further_readings {
            furtherReading: further_reading {
              text
            }
          }

          footnotes {
            footnote {
              text
            }
          }
          footerVideo: footer_video_id {
            text
          }
          discussionQuestions: discussion_questions {
            discussionQuestion: discussion_question {
              text
            }
          }
        }
      }
    }
    totalWatchData: allPrismicEpisodes(sort: { fields: data___priority, order: ASC }) {
      nodes {
        data {
          title {
            text
          }
          videoDuration: video_duration {
            text
          }
          thumbnailImage: thumbnail_image {
            url
          }
          youtubeId: youtube_id {
            text
          }
        }
        uid
      }
    }
    podCastData: allPrismicPodcast(sort: { fields: data___priority, order: ASC }) {
      nodes {
        data {
          thumnailImage: thumbnail_image {
            url
          }
          podCastTitle: podcast_title {
            text
          }
          season {
            type
            uid
          }
          podcastDuration: podcast_duration
          priority
        }
        uid
      }
    }
    readData: allPrismicRead(sort: { fields: data___priority }) {
      nodes {
        data {
          title {
            text
          }
          readDuration: read_duration1 {
            text
          }
          featuredImage: featured_image {
            alt
            url
            fluid(maxWidth: 600) {
              ...GatsbyPrismicImageFluid
            }
          }
        }
        url
        uid
      }
    }
  }
`;
